import { useCallback, useEffect, useMemo, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import moment from "moment";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import { useTranslation } from "react-i18next";

import {
  BulkDeleteSubscriptionCancellationSurveysCall,
  DeleteSubscriptionCancellationSurveyCall,
  GetSubscriptionCancellationSurveysCall,
} from "../../redux/api/subscriptionCancellationSurveys.api";

const REASON_KEYS = [
  "too_expensive",
  "better_alternative",
  "not_using_enough",
  "missing_features",
  "technical_issues",
  "achieved_goal",
];

const headCellSx = {
  py: 2,
  px: 2,
  fontSize: "0.75rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "text.secondary",
  borderBottom: "1px solid",
  borderColor: "divider",
  bgcolor: "grey.50",
};

const bodyCellSx = {
  py: 2,
  px: 2,
  fontSize: "0.875rem",
  verticalAlign: "middle",
  borderColor: "divider",
};

function DetailLine({ label, value }) {
  const display =
    value === null || value === undefined || value === "" ? "—" : String(value);
  return (
    <Grid container spacing={1} sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
      <Grid item xs={12} sm={4}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={8}>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {display}
        </Typography>
      </Grid>
    </Grid>
  );
}

export default function UnsubscribeReportPage() {
  const { t } = useTranslation();
  const yn = (v) =>
    v === true ? t("unsubscribeReport.boolYes") : v === false ? t("unsubscribeReport.boolNo") : "—";

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selected, setSelected] = useState(() => new Set());
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, ids: [] });
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    title: "",
    color: "success",
  });

  const reasonLabels = useMemo(() => {
    const m = {};
    REASON_KEYS.forEach((k) => {
      m[k] = t(`unsubscribeReport.reasons.${k}`, { defaultValue: k });
    });
    return m;
  }, [t]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    const offset = page * rowsPerPage;
    const { rows: data, total: count } = await GetSubscriptionCancellationSurveysCall({
      limit: rowsPerPage,
      offset,
    });
    setRows(Array.isArray(data) ? data : []);
    setTotal(typeof count === "number" ? count : 0);
    setLoading(false);
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const rowIdsOnPage = useMemo(() => rows.map((r) => r.id), [rows]);
  const allSelected =
    rowIdsOnPage.length > 0 && rowIdsOnPage.every((id) => selected.has(id));
  const someSelected = rowIdsOnPage.some((id) => selected.has(id));

  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        rowIdsOnPage.forEach((id) => next.delete(id));
      } else {
        rowIdsOnPage.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const openRowMenu = (e, row) => {
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };

  const closeRowMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const openDeleteConfirm = (ids) => {
    setDeleteDialog({ open: true, ids: [...ids] });
  };

  const runDelete = async () => {
    const ids = deleteDialog.ids;
    if (!ids.length) return;
    setDeleteSubmitting(true);
    let ok = false;
    if (ids.length === 1) {
      const r = await DeleteSubscriptionCancellationSurveyCall(ids[0]);
      ok = r.ok;
    } else {
      const r = await BulkDeleteSubscriptionCancellationSurveysCall(ids);
      ok = r.ok;
    }
    setDeleteSubmitting(false);
    setDeleteDialog({ open: false, ids: [] });
    if (ok) {
      setSnack({
        open: true,
        title: t("unsubscribeReport.deleteSuccess"),
        color: "success",
      });
      clearSelection();
      closeRowMenu();
      setDetailRow(null);
      await fetchRows();
    } else {
      setSnack({
        open: true,
        title: t("unsubscribeReport.deleteError"),
        color: "error",
      });
    }
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    clearSelection();
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
    clearSelection();
  };

  const userDisplay = (row) => {
    if (row.user?.email) return row.user.email;
    if (row.user_email) return row.user_email;
    if (row.user_id) return row.user_id;
    return "—";
  };

  const formatDt = (v) =>
    v ? moment(v).format("YYYY-MM-DD HH:mm") : "—";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        component="main"
        sx={{
          py: { xs: 3, md: 4 },
          px: { xs: 2, sm: 3, md: 4 },
          maxWidth: 1440,
          mx: "auto",
        }}
      >
        <Stack spacing={2.5} sx={{ mb: 3 }}>
          <Box>
            <MDTypography variant="h4" fontWeight="bold" color="text" sx={{ letterSpacing: "-0.02em" }}>
              {t("unsubscribeReport.title")}
            </MDTypography>
            <MDTypography
              variant="body1"
              color="text"
              sx={{ mt: 1, maxWidth: 720, lineHeight: 1.6, opacity: 0.85 }}
            >
              {t("unsubscribeReport.subtitle")}
            </MDTypography>
          </Box>

          {selected.size > 0 ? (
            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "primary.light",
                bgcolor: (theme) => theme.palette.primary.main + "08",
              }}
            >
              <Toolbar disableGutters variant="dense" sx={{ minHeight: 48, gap: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {t("unsubscribeReport.selectedCount", { count: selected.size })}
                </Typography>
                <Button
                  color="error"
                  variant="contained"
                  size="small"
                  onClick={() => openDeleteConfirm([...selected])}
                >
                  {t("unsubscribeReport.deleteSelected")}
                </Button>
                <Button size="small" onClick={clearSelection}>
                  {t("unsubscribeReport.clearSelection")}
                </Button>
              </Toolbar>
            </Paper>
          ) : null}

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 8px 32px rgba(0,0,0,0.35)"
                  : "0 12px 40px rgba(15, 23, 42, 0.06)",
            }}
          >
            <TableContainer>
              {loading ? (
                <Box display="flex" justifyContent="center" py={8}>
                  <CircularProgress size={36} />
                </Box>
              ) : (
                <Table size="medium" sx={{ minWidth: 880 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ ...headCellSx, width: 48 }}>
                        <Checkbox
                          indeterminate={someSelected && !allSelected}
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          inputProps={{ "aria-label": t("unsubscribeReport.deleteSelected") }}
                        />
                      </TableCell>
                      <TableCell sx={headCellSx}>{t("unsubscribeReport.columns.date")}</TableCell>
                      <TableCell sx={headCellSx}>{t("unsubscribeReport.columns.user")}</TableCell>
                      <TableCell sx={headCellSx}>{t("unsubscribeReport.columns.reason")}</TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: 200 }}>
                        {t("unsubscribeReport.columns.feedback")}
                      </TableCell>
                      <TableCell sx={{ ...headCellSx, width: 56 }} align="right">
                        {t("unsubscribeReport.columns.actions")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ ...bodyCellSx, py: 6 }}>
                          <Typography variant="body2" color="text.secondary" align="center">
                            {t("unsubscribeReport.empty")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          selected={selected.has(row.id)}
                          sx={{ "&:last-child td": { borderBottom: 0 } }}
                        >
                          <TableCell padding="checkbox" sx={bodyCellSx}>
                            <Checkbox
                              checked={selected.has(row.id)}
                              onChange={() => toggleOne(row.id)}
                              inputProps={{ "aria-labelledby": `survey-${row.id}` }}
                            />
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Typography id={`survey-${row.id}`} variant="body2" fontWeight={500}>
                              {formatDt(row.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                              {userDisplay(row)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Chip
                              size="small"
                              label={reasonLabels[row.reason] || row.reason || "—"}
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, maxWidth: 360 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                wordBreak: "break-word",
                              }}
                            >
                              {row.feedback_text || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={bodyCellSx}>
                            <IconButton
                              size="small"
                              aria-label={t("unsubscribeReport.actionsMenu")}
                              onClick={(e) => openRowMenu(e, row)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage={t("unsubscribeReport.rowsPerPage")}
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                px: 2,
                py: 1,
              }}
            />
          </Paper>
        </Stack>
      </MDBox>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeRowMenu}>
        <MenuItem
          onClick={() => {
            setDetailRow(menuRow);
            closeRowMenu();
          }}
        >
          {t("unsubscribeReport.moreInfo")}
        </MenuItem>
        <MenuItem
          sx={{ color: "error.main" }}
          onClick={() => {
            if (menuRow?.id) openDeleteConfirm([menuRow.id]);
            closeRowMenu();
          }}
        >
          {t("unsubscribeReport.deleteOne")}
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h5" fontWeight="bold">
            {t("unsubscribeReport.detailTitle")}
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {detailRow ? (
            <Stack spacing={3}>
              <Box>
                <MDTypography variant="button" color="info" fontWeight="bold" sx={{ mb: 1, display: "block" }}>
                  {t("unsubscribeReport.surveySection")}
                </MDTypography>
                <DetailLine label={t("unsubscribeReport.detail.surveyId")} value={detailRow.id} />
                <DetailLine label={t("unsubscribeReport.detail.userId")} value={detailRow.user_id} />
                <DetailLine label={t("unsubscribeReport.detail.createdAt")} value={formatDt(detailRow.created_at)} />
                <DetailLine label={t("unsubscribeReport.detail.updatedAt")} value={formatDt(detailRow.updated_at)} />
                <DetailLine
                  label={t("unsubscribeReport.detail.reason")}
                  value={reasonLabels[detailRow.reason] || detailRow.reason}
                />
                <DetailLine label={t("unsubscribeReport.detail.feedback")} value={detailRow.feedback_text} />
                <DetailLine label={t("unsubscribeReport.detail.product")} value={detailRow.product_identifier} />
                <DetailLine label={t("unsubscribeReport.detail.platform")} value={detailRow.platform} />
                <DetailLine
                  label={t("unsubscribeReport.detail.retentionOffer")}
                  value={detailRow.retention_offer_type}
                />
                <DetailLine
                  label={t("unsubscribeReport.detail.retentionAccepted")}
                  value={yn(detailRow.retention_accepted)}
                />
                <DetailLine label={t("unsubscribeReport.detail.wantsPause")} value={yn(detailRow.wants_pause)} />
                <DetailLine label={t("unsubscribeReport.detail.wantsContact")} value={yn(detailRow.wants_contact)} />
                <DetailLine
                  label={t("unsubscribeReport.detail.systemCancel")}
                  value={yn(detailRow.proceeded_to_system_cancel)}
                />
              </Box>

              <Box>
                <MDTypography variant="button" color="info" fontWeight="bold" sx={{ mb: 1, display: "block" }}>
                  {t("unsubscribeReport.accountSection")}
                </MDTypography>
                {!detailRow.user ? (
                  <Typography variant="body2" color="text.secondary">
                    {t("unsubscribeReport.noUser")}
                  </Typography>
                ) : (
                  <>
                    <DetailLine label={t("unsubscribeReport.detail.email")} value={detailRow.user.email} />
                    <DetailLine
                      label={t("unsubscribeReport.detail.firstName")}
                      value={detailRow.user.first_name}
                    />
                    <DetailLine label={t("unsubscribeReport.detail.lastName")} value={detailRow.user.last_name} />
                    <DetailLine label={t("unsubscribeReport.detail.role")} value={detailRow.user.role} />
                    <DetailLine label={t("unsubscribeReport.detail.status")} value={detailRow.user.status} />
                    <DetailLine
                      label={t("unsubscribeReport.detail.currentSubscription")}
                      value={detailRow.user.current_subscription}
                    />
                    <DetailLine label={t("unsubscribeReport.detail.country")} value={detailRow.user.country} />
                    <DetailLine label={t("unsubscribeReport.detail.university")} value={detailRow.user.university} />
                    <DetailLine
                      label={t("unsubscribeReport.detail.universityId")}
                      value={detailRow.user.university_id}
                    />
                    <DetailLine label={t("unsubscribeReport.detail.yearStudy")} value={detailRow.user.year_study} />
                    <DetailLine label={t("unsubscribeReport.detail.occupation")} value={detailRow.user.occupation} />
                    <DetailLine
                      label={t("unsubscribeReport.detail.socialAuth")}
                      value={detailRow.user.social_auth_provider}
                    />
                    <DetailLine
                      label={t("unsubscribeReport.detail.profileVerified")}
                      value={yn(detailRow.user.profile_verified)}
                    />
                    <DetailLine label={t("unsubscribeReport.detail.appVersion")} value={detailRow.user.version} />
                    <DetailLine label={t("unsubscribeReport.detail.osVersion")} value={detailRow.user.os_version} />
                    <DetailLine
                      label={t("unsubscribeReport.detail.deviceModel")}
                      value={detailRow.user.device_model}
                    />
                    <DetailLine
                      label={t("unsubscribeReport.detail.accountCreated")}
                      value={formatDt(detailRow.user.created_at)}
                    />
                    <DetailLine label={t("unsubscribeReport.detail.about")} value={detailRow.user.about} />
                    <DetailLine label={t("unsubscribeReport.detail.course")} value={detailRow.user.course} />
                    <DetailLine
                      label={t("unsubscribeReport.detail.enabledEarning")}
                      value={yn(detailRow.user.enabled_earning)}
                    />
                  </>
                )}
              </Box>

              {detailRow?.user?.universityInfo ? (
                <Box>
                  <MDTypography variant="button" color="info" fontWeight="bold" sx={{ mb: 1, display: "block" }}>
                    {t("unsubscribeReport.universitySection")}
                  </MDTypography>
                  <DetailLine
                    label={t("unsubscribeReport.detail.uniName")}
                    value={detailRow.user.universityInfo.name}
                  />
                  <DetailLine
                    label={t("unsubscribeReport.detail.uniStatus")}
                    value={detailRow.user.universityInfo.status}
                  />
                  <DetailLine
                    label={t("unsubscribeReport.detail.uniLicenseEnd")}
                    value={formatDt(detailRow.user.universityInfo.license_end)}
                  />
                </Box>
              ) : null}

              <Box>
                <MDTypography variant="button" color="text" fontWeight="bold" sx={{ mb: 1, display: "block" }}>
                  {t("unsubscribeReport.rawSection")}
                </MDTypography>
                <Box
                  component="pre"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "grey.100",
                    fontSize: "0.72rem",
                    overflow: "auto",
                    maxHeight: 280,
                    m: 0,
                  }}
                >
                  {JSON.stringify(detailRow, null, 2)}
                </Box>
              </Box>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDetailRow(null)}>{t("common.close")}</Button>
          {detailRow?.id ? (
            <Button color="error" variant="contained" onClick={() => openDeleteConfirm([detailRow.id])}>
              {t("unsubscribeReport.deleteOne")}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog.open} onClose={() => !deleteSubmitting && setDeleteDialog({ open: false, ids: [] })}>
        <DialogTitle>{t("unsubscribeReport.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t("unsubscribeReport.confirmDeleteBody", { count: deleteDialog.ids.length })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, ids: [] })} disabled={deleteSubmitting}>
            {t("common.cancel")}
          </Button>
          <Button color="error" variant="contained" onClick={runDelete} disabled={deleteSubmitting}>
            {deleteSubmitting ? t("common.loading") : t("unsubscribeReport.confirmDelete")}
          </Button>
        </DialogActions>
      </Dialog>

      <MDSnackbar
        color={snack.color}
        icon={snack.color === "success" ? "check" : "warning"}
        title={snack.title}
        dateTime=""
        content=""
        open={snack.open}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        close={() => setSnack((s) => ({ ...s, open: false }))}
        bgWhite
      />

      <Footer />
    </DashboardLayout>
  );
}
